import { createContext, useContext } from 'react';
import { useSocket } from '../hooks/useSocket.ts';
import { usePresence } from '../hooks/usePresence.ts';
import { useAuth } from '../hooks/useAuth.ts';
import useSettings from '../hooks/useSettings.ts';
import type { UsePresenceReturn } from '../hooks/usePresence.ts';

const PresenceContext = createContext<UsePresenceReturn>({
  online: [],
  pending: new Set(),
  sendChallenge: () => {},
  refresh: () => {},
  selfUserId: null,
  self: null,
});

export function usePresenceContext(): UsePresenceReturn {
  return useContext(PresenceContext);
}

export function PresenceProvider({ children }: { children: React.ReactNode }) {
  const { socket } = useSocket();
  const { user } = useAuth();
  const { appearOnline } = useSettings();
  const presence = usePresence(socket, user?.sub ?? null);

  // When the user opts out of appearing online, suppress their own green dot —
  // others already don't see them (server-side), this keeps it consistent for
  // the user themselves (their badge/profile show no dot).
  const value = appearOnline ? presence : { ...presence, selfUserId: null, self: null };

  return (
    <PresenceContext.Provider value={value}>
      {children}
    </PresenceContext.Provider>
  );
}
